import {
  SalesDistribution,
  type ThemeColor,
  type UniverseName,
  assertNever,
  universeEnum,
} from "@pharmupp/p3-front-commons"
import type { Universe, Universes } from "./salesRxSplit.type"

const RX_UNIVERSE_NAME: UniverseName = "RX_MEDICATIONS"

const getMainStat = (universe: Universe, distribution: SalesDistribution) => {
  const getValue = (value: undefined | null | number) => Math.max(value ?? 0, 0)

  switch (distribution) {
    case SalesDistribution.SALES:
      return getValue(universe.sales?.mainStat)
    case SalesDistribution.MARGIN:
      return getValue(universe.margin?.mainStat)
    default:
      return assertNever(
        distribution,
        0,
        "getMainStat => SalesRxSplitSection/utils.ts",
      )
  }
}

const unknownUniverseColor: ThemeColor = ({ palette }) => palette.grey.dark

/**
 * Display attributes of a universe, with a fallback for a universe returned by
 * the API but not yet referenced in universeEnum
 */
export const getUniverseDisplay = (name: UniverseName) => {
  const universe: (typeof universeEnum)[UniverseName] | undefined =
    universeEnum[name]
  return {
    color: universe?.color ?? unknownUniverseColor,
    shortLabel: universe?.shortLabel ?? name,
  }
}

export const getRxSplitDisplay = ({
  universes,
  distribution,
}: { distribution: SalesDistribution } & Universes) => {
  const rxUniverse = universes.find((u) => u.name === RX_UNIVERSE_NAME)
  const rxValue = rxUniverse ? getMainStat(rxUniverse, distribution) : 0
  const noRxUniverses = universes
    .filter((u) => u.name !== RX_UNIVERSE_NAME)
    .map((u) => ({ name: u.name, value: getMainStat(u, distribution) }))
  const noRxTotalSales = noRxUniverses.reduce((sum, u) => sum + u.value, 0)
  const totalSales = rxValue + noRxTotalSales

  const noRx = noRxUniverses.map(({ name, value }) => ({
    name,
    value,
    totalPercent: toPercentage(value, totalSales),
    percent: toPercentage(value, noRxTotalSales),
  }))

  return {
    rx: {
      value: rxValue,
      universes: undefined,
      percent: toPercentage(rxValue, totalSales),
    },
    noRx: {
      value: noRxTotalSales,
      universes: noRx,
      percent: toPercentage(noRxTotalSales, totalSales),
    },
  }
}

type SplitDisplay = ReturnType<typeof getRxSplitDisplay>

export const isEmptySection = ({ rx, noRx }: SplitDisplay) =>
  !rx.value && !noRx.value

const toPercentage = (value: number, total: number) =>
  total ? (value * 100) / total : 0
